import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import dayCloseService from '../../services/dayCloseService';
import shiftService from '../../services/shiftService';
import toast from 'react-hot-toast';
import DenominationPanel, {
  getDenominationTotal,
  serializeDenomination,
  deserializeDenomination,
  DenominationView,
} from '../../components/POS/Shift/DenominationPanel';
import { useAuth } from '@/context/AuthContext';
import { P } from '@/config/permissionCodes';
import ShiftReportModal from '../../components/POS/DayClose/ShiftReportModal';



import {
  CalendarCheck,
  IndianRupee,
  Receipt,
  Landmark,
  Banknote,
  Smartphone,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Loader2,
  RefreshCw,
  X,
  Download,
  History,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LockOpen,
  Clock,
  Users,
  MoreVertical,
  Eye,
} from 'lucide-react';

export default function DayClosePage() {
  const { hasPermission } = useAuth();
  const canView = hasPermission(P.BACK_OFFICE.DAY_CLOSE.SCREEN);
  const [activeTab, setActiveTab] = useState('today');
  const [showShiftReport, setShowShiftReport] = useState(false);

  if (!canView) {
    return (
      <AdminLayout>
        <AccessDenied />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {/* Sticky Page Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="flex items-center justify-between px-6 py-3">
            {/* Left — Icon + Title */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-sm">
                <CalendarCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 leading-tight">Day Close</h1>
                <p className="text-xs text-gray-500">End-of-day settlement and reconciliation</p>
              </div>
            </div>

            {/* Right — Tabs */}
           <div className="flex items-center gap-3">
  <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">

              <button
                onClick={() => setActiveTab('today')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  activeTab === 'today'
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <CalendarCheck className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                Today
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  activeTab === 'history'
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <History className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                History
              </button>
            </div>
            <button
    onClick={() => setShowShiftReport(true)}
    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 transition"
  >
    <Receipt className="w-3.5 h-3.5" />
    Shift Report
  </button>
</div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 space-y-5">
          {activeTab === 'today' ? <TodayTab /> : <HistoryTab />}
        </div>

        <ShiftReportModal
          isOpen={showShiftReport}
          onClose={() => setShowShiftReport(false)}
          restaurantName={/* pull from your auth/branch context if available */ 'Restaurant'}
        />
        
      </div>
    </AdminLayout>
  );
}

// ─── Access Denied helper ────────────────────────────────────────────────────

function AccessDenied() {
  return (
    <div className="flex items-center justify-center py-24 text-center">
      <div>
        <p className="text-lg font-semibold text-gray-700">Access Denied</p>
        <p className="text-sm text-gray-500 mt-1">You don't have permission to view this page.</p>
      </div>
    </div>
  );
}

// ─── Today Tab ──────────────────────────────────────────────────────────────

function TodayTab() {
  const { hasPermission } = useAuth();

  // Permission flags
  const canClose          = hasPermission(P.BACK_OFFICE.DAY_CLOSE.CLOSE);
  const canForceCloseShift = hasPermission(P.BACK_OFFICE.DAY_CLOSE.FORCE_CLOSE_SHIFT);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [notes, setNotes] = useState('');
  const [actualCash, setActualCash] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showReopen, setShowReopen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dayCloseService.getSummary();
      setSummary(res.data || res);
    } catch {
      toast.error('Failed to load day close summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleExecute = async () => {
    if (!summary) return;
    const cashVal =
      summary.cashHandoverEnabled && actualCash !== ''
        ? parseFloat(actualCash)
        : null;
    setExecuting(true);
    try {
      const res = await dayCloseService.executeDayClose(
        summary.businessDate,
        notes,
        cashVal
      );
      setSummary(res.data || res);
      setShowConfirm(false);
      setNotes('');
      setActualCash('');
      toast.success('Day closed successfully!');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        'Failed to close day';
      toast.error(msg);
    } finally {
      setExecuting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!summary?.businessDate) return;
    setDownloading(true);
    try {
      const res = await dayCloseService.downloadPdf(summary.businessDate);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `day-close-${summary.businessDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleReopen = async () => {
    if (!summary?.businessDate || !reopenReason.trim()) return;
    setReopening(true);
    try {
      const res = await dayCloseService.reopenDay(summary.businessDate, reopenReason.trim());
      setSummary(res.data || res);
      setShowReopen(false);
      setReopenReason('');
      toast.success('Day reopened successfully');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        'Failed to reopen day';
      toast.error(msg);
    } finally {
      setReopening(false);
    }
  };

  const [forceClosing, setForceClosing] = useState(null);
  const [forceCloseTarget, setForceCloseTarget] = useState(null);
  const [forceCloseActualCash, setForceCloseActualCash] = useState('');
  const [forceCloseNotes, setForceCloseNotes] = useState('');
  const [forceCloseDenomCounts, setForceCloseDenomCounts] = useState({});
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [denomViewTarget, setDenomViewTarget] = useState(null);

  const denominationEnabled = summary?.denominationEnabled === true;

  useEffect(() => {
    if (denominationEnabled && forceCloseTarget) {
      const total = getDenominationTotal(forceCloseDenomCounts);
      setForceCloseActualCash(total > 0 ? String(total) : '');
    }
  }, [forceCloseDenomCounts, denominationEnabled, forceCloseTarget]);

  useEffect(() => {
    if (menuOpenId == null) return;
    const handler = () => setMenuOpenId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpenId]);

  const handleForceClose = async () => {
    if (!forceCloseTarget) return;
    const shiftId = forceCloseTarget.shiftId;
    setForceClosing(shiftId);
    try {
      const params = {};
      if (forceCloseActualCash !== '') params.actualCash = parseFloat(forceCloseActualCash);
      if (forceCloseNotes.trim()) params.notes = forceCloseNotes.trim();
      if (denominationEnabled) {
        const denomJson = serializeDenomination(forceCloseDenomCounts);
        if (denomJson) params.denomination = denomJson;
      }
      await shiftService.forceCloseShift(shiftId, params);
      toast.success('Shift force-closed successfully');
      setForceCloseTarget(null);
      setForceCloseActualCash('');
      setForceCloseNotes('');
      setForceCloseDenomCounts({});
      fetchSummary();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        'Failed to force close shift';
      toast.error(msg);
    } finally {
      setForceClosing(null);
    }
  };

  const isClosed = summary?.status === 'CLOSED';
  const shiftEnabled = summary?.shiftEnabled === true;
  const hasOpenShifts = (summary?.openShiftCount || 0) > 0;
  const hasRunningOrders = (summary?.runningOrderCount || 0) > 0;
  const hasBlockers = (summary?.openBillCount || 0) > 0 || hasRunningOrders || (shiftEnabled && hasOpenShifts);
  // canCloseDay combines the RBAC permission AND the business-logic blockers
  const canCloseDay = canClose && !isClosed && !hasBlockers;

  const cashHandoverEnabled = summary?.cashHandoverEnabled === true;
  const cashDiff =
    actualCash !== '' ? parseFloat(actualCash) - Number(summary?.cashTotal || 0) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Loading summary...</p>
        </div>
      </div>
    );
  }

  const businessDateFormatted = summary?.businessDate
    ? new Date(summary.businessDate + 'T00:00:00').toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

  return (
    <>
      {/* Date bar + status + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-700">{businessDateFormatted}</span>
          <span
            className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${
              isClosed
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {isClosed ? 'Closed' : 'Open'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchSummary}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition border border-transparent hover:border-slate-200"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          {/* PDF download — always visible when closed (view-level action) */}
          {isClosed && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white hover:bg-slate-50 rounded-lg transition border border-slate-200 disabled:opacity-50"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              PDF
            </button>
          )}

          {/* Reopen — requires CLOSE permission (it's a write action) */}
          {isClosed && canClose && (
            <button
              onClick={() => setShowReopen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition border border-amber-200"
            >
              <LockOpen className="w-3.5 h-3.5" />
              Reopen
            </button>
          )}

          {/* Close Day — requires CLOSE permission */}
          {!isClosed && canClose && (
            <button
              onClick={() => setShowConfirm(true)}
              disabled={!canCloseDay}
              className={`px-4 py-1.5 rounded-lg font-semibold text-sm transition ${
                canCloseDay
                  ? 'bg-teal-600 text-white hover:bg-teal-700'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              Close Day
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <SummaryCard icon={IndianRupee} label="Revenue" value={`₹${fmt(summary?.totalRevenue)}`} color="teal" />
        <SummaryCard icon={Receipt} label="Bills" value={summary?.totalBills ?? 0} color="blue" />
        <SummaryCard icon={Landmark} label="Tax" value={`₹${fmt(summary?.totalTax)}`} color="violet" />
        <SummaryCard icon={Banknote} label="Cash" value={`₹${fmt(summary?.cashTotal)}`} color="emerald" />
        <SummaryCard icon={Smartphone} label="Online" value={`₹${fmt(summary?.onlineTotal)}`} color="sky" />
      </div>

      {/* Validation + Closed info */}
      <ValidationPanel
        openBillCount={summary?.openBillCount || 0}
        runningOrderCount={summary?.runningOrderCount || 0}
        unpaidBillCount={summary?.unpaidBillCount || 0}
        isClosed={isClosed}
        summary={summary}
      />

      {/* Shift Breakdown */}
      {(shiftEnabled || summary?.shifts?.length > 0) && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-700">Shift Breakdown</h2>
            </div>
            <div className="flex items-center gap-2">
              {hasOpenShifts && !isClosed && (
                <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                  {summary.openShiftCount} open
                </span>
              )}
              <span className="text-[11px] text-slate-400">
                {summary?.shifts?.length || 0} shift(s)
              </span>
            </div>
          </div>
          {summary?.shifts?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-slate-50/80 text-slate-500 text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 font-medium">User</th>
                    <th className="text-left px-4 py-2.5 font-medium">Status</th>
                    <th className="text-left px-4 py-2.5 font-medium">Start</th>
                    <th className="text-left px-4 py-2.5 font-medium">End</th>
                    <th className="text-right px-4 py-2.5 font-medium">Opening</th>
                    <th className="text-right px-4 py-2.5 font-medium">Sales</th>
                    <th className="text-right px-4 py-2.5 font-medium">Cash</th>
                    <th className="text-right px-4 py-2.5 font-medium">Online</th>
                    <th className="text-right px-4 py-2.5 font-medium">Expected</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actual</th>
                    <th className="text-right px-4 py-2.5 font-medium">Diff</th>
                    {/* Actions column only shown if the user can view denomination OR force close */}
                    <th className="text-center px-2 py-2.5 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {summary.shifts.map((s, idx) => {
                    const isOpen = s.status === 'OPEN';
                    const isLast = idx === summary.shifts.length - 1;
                    // Whether this row's context menu has any items to show
                    const hasMenuItems = s.denomination || (isOpen && !isClosed && canForceCloseShift);
                    return (
                      <tr key={s.shiftId} className="hover:bg-slate-50/50 transition">
                        <td className="px-4 py-2.5 font-medium text-slate-700">{s.userName || '—'}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            s.status === 'CLOSED' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                          }`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs tabular-nums">
                          {s.startedAt ? new Date(s.startedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs tabular-nums">
                          {s.endedAt ? new Date(s.endedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">₹{fmt(s.openingCash)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-800 tabular-nums">₹{fmt(s.totalSales)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">₹{fmt(s.cashSales)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">₹{fmt(s.onlineSales)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">
                          {s.expectedCash != null ? `₹${fmt(s.expectedCash)}` : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">
                          {s.actualCash != null ? `₹${fmt(s.actualCash)}` : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {s.cashDifference != null ? (
                            <span className={`font-medium ${
                              Number(s.cashDifference) === 0 ? 'text-emerald-600' :
                              Number(s.cashDifference) < 0 ? 'text-red-600' : 'text-amber-600'
                            }`}>
                              ₹{fmt(s.cashDifference)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          {hasMenuItems && (
                            <div className="relative inline-block">
                              <button
                                onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === s.shiftId ? null : s.shiftId); }}
                                className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                              {menuOpenId === s.shiftId && (
                                <div
                                  className={`absolute right-0 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 ${
                                    isLast ? 'bottom-full mb-1' : 'top-full mt-1'
                                  }`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {/* View Denomination — always visible (read-only) */}
                                  {s.denomination && (
                                    <button
                                      onClick={() => { setDenomViewTarget(s); setMenuOpenId(null); }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition"
                                    >
                                      <Eye className="w-3.5 h-3.5 text-slate-400" />
                                      View Denomination
                                    </button>
                                  )}
                                  {/* Force Close — requires FORCE_CLOSE_SHIFT permission */}
                                  {isOpen && !isClosed && canForceCloseShift && (
                                    <button
                                      onClick={() => { setForceCloseTarget(s); setMenuOpenId(null); }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition"
                                    >
                                      <Lock className="w-3.5 h-3.5" />
                                      Force Close
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-5 py-10 text-center">
              <Clock className="w-7 h-7 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No shifts recorded for this date</p>
            </div>
          )}
        </div>
      )}

      {/* Open shifts warning */}
      {shiftEnabled && hasOpenShifts && !isClosed && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3.5 flex items-center gap-3">
          <AlertTriangle className="w-4.5 h-4.5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            <span className="font-semibold">{summary.openShiftCount} shift(s)</span> are still open. Close all shifts before closing the day.
          </p>
        </div>
      )}

      {/* Payment Breakdown */}
      {summary?.paymentModeBreakdown?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
            <Banknote className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-700">Payment Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-slate-50/80 text-slate-500 text-[11px] uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 font-medium">Mode</th>
                  <th className="text-left px-4 py-2.5 font-medium">Account</th>
                  <th className="text-right px-4 py-2.5 font-medium">Transactions</th>
                  <th className="text-right px-4 py-2.5 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {summary.paymentModeBreakdown.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50/50 transition">
                    <td className="px-4 py-2.5 font-medium text-slate-700">{row.paymentMode}</td>
                    <td className="px-4 py-2.5 text-slate-500">{row.accountName || '—'}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{row.transactionCount}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-800 tabular-nums">₹{fmt(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Day Close Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Confirm Day Close</h3>
              <button onClick={() => setShowConfirm(false)} className="p-1 rounded-lg hover:bg-slate-100 transition">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-slate-600">
                You are about to close the business day for{' '}
                <span className="font-semibold text-slate-800">{businessDateFormatted}</span>.
                This action cannot be undone.
              </p>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500 space-y-1">
                <div className="flex justify-between">
                  <span>Revenue</span>
                  <span className="font-semibold text-slate-700">₹{fmt(summary?.totalRevenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Bills</span>
                  <span className="font-semibold text-slate-700">{summary?.totalBills}</span>
                </div>
              </div>

              {cashHandoverEnabled && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Cash Handover</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Expected Cash</span>
                    <span className="font-semibold text-slate-800">₹{fmt(summary?.cashTotal)}</span>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Actual Cash <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={actualCash}
                      onChange={(e) => setActualCash(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                    />
                  </div>
                  {cashDiff !== null && !isNaN(cashDiff) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Difference</span>
                      <span className={`font-semibold ${cashDiff === 0 ? 'text-emerald-600' : cashDiff < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                        ₹{fmt(cashDiff)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Any remarks for this day close..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 flex items-center justify-end gap-3">
              <button onClick={() => setShowConfirm(false)} disabled={executing} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition">
                Cancel
              </button>
              <button
                onClick={handleExecute}
                disabled={executing || (cashHandoverEnabled && actualCash === '')}
                className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition flex items-center gap-2 disabled:opacity-60"
              >
                {executing && <Loader2 className="w-4 h-4 animate-spin" />}
                {executing ? 'Closing...' : 'Confirm & Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Force Close Confirmation Modal */}
      {forceCloseTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className={`bg-white rounded-2xl shadow-2xl w-full mx-4 overflow-hidden flex flex-col ${denominationEnabled ? 'max-w-4xl max-h-[90vh]' : 'max-w-md'}`}>
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-bold text-slate-800">Force Close Shift</h3>
              <button
                onClick={() => { setForceCloseTarget(null); setForceCloseActualCash(''); setForceCloseNotes(''); setForceCloseDenomCounts({}); }}
                className="p-1 rounded-lg hover:bg-slate-100 transition"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {denominationEnabled ? (
                <div className="flex">
                  <div className="flex-1 px-6 py-5 space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2.5">
                      <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-700">
                        You are about to force-close <span className="font-semibold">{forceCloseTarget.userName}</span>'s shift without the user's input.
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500 space-y-1.5">
                      <div className="flex justify-between"><span>Opening Cash</span><span className="font-semibold text-slate-700">₹{fmt(forceCloseTarget.openingCash)}</span></div>
                      <div className="flex justify-between"><span>Total Sales</span><span className="font-semibold text-slate-700">₹{fmt(forceCloseTarget.totalSales)}</span></div>
                      <div className="flex justify-between"><span>Cash Sales</span><span className="font-semibold text-slate-700">₹{fmt(forceCloseTarget.cashSales)}</span></div>
                      <div className="flex justify-between"><span>Online Sales</span><span className="font-semibold text-slate-700">₹{fmt(forceCloseTarget.onlineSales)}</span></div>
                      <div className="flex justify-between border-t border-slate-200 pt-1.5">
                        <span className="font-medium text-slate-600">Expected Cash</span>
                        <span className="font-bold text-slate-800">₹{fmt(forceCloseTarget.expectedCash)}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Notes (optional)</label>
                      <textarea
                        value={forceCloseNotes}
                        onChange={(e) => setForceCloseNotes(e.target.value)}
                        rows={2}
                        placeholder="Reason for force closing..."
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 resize-none"
                      />
                    </div>
                  </div>
                  <div className="w-[360px] flex-shrink-0 border-l border-slate-200 bg-slate-50/40 px-5 py-5">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Cash Denomination</p>
                    <DenominationPanel
                      counts={forceCloseDenomCounts}
                      onChange={setForceCloseDenomCounts}
                      expectedCash={Number(forceCloseTarget.expectedCash || 0)}
                    />
                  </div>
                </div>
              ) : (
                <div className="px-6 py-5 space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-700">
                      You are about to force-close <span className="font-semibold">{forceCloseTarget.userName}</span>'s shift without the user's input.
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500 space-y-1.5">
                    <div className="flex justify-between"><span>Opening Cash</span><span className="font-semibold text-slate-700">₹{fmt(forceCloseTarget.openingCash)}</span></div>
                    <div className="flex justify-between"><span>Total Sales</span><span className="font-semibold text-slate-700">₹{fmt(forceCloseTarget.totalSales)}</span></div>
                    <div className="flex justify-between"><span>Cash Sales</span><span className="font-semibold text-slate-700">₹{fmt(forceCloseTarget.cashSales)}</span></div>
                    <div className="flex justify-between"><span>Online Sales</span><span className="font-semibold text-slate-700">₹{fmt(forceCloseTarget.onlineSales)}</span></div>
                    <div className="flex justify-between border-t border-slate-200 pt-1.5">
                      <span className="font-medium text-slate-600">Expected Cash</span>
                      <span className="font-bold text-slate-800">₹{fmt(forceCloseTarget.expectedCash)}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Actual Cash (optional)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">₹</span>
                      <input
                        type="number"
                        value={forceCloseActualCash}
                        onChange={(e) => setForceCloseActualCash(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        className="w-full pl-8 pr-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400"
                      />
                    </div>
                    {forceCloseActualCash !== '' && forceCloseTarget.expectedCash != null && (() => {
                      const diff = parseFloat(forceCloseActualCash) - Number(forceCloseTarget.expectedCash || 0);
                      return (
                        <div className="flex justify-between text-xs mt-1.5">
                          <span className="text-slate-400">Difference</span>
                          <span className={`font-semibold ${diff === 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                            ₹{fmt(diff)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Notes (optional)</label>
                    <textarea
                      value={forceCloseNotes}
                      onChange={(e) => setForceCloseNotes(e.target.value)}
                      rows={2}
                      placeholder="Reason for force closing..."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 resize-none"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-slate-50 flex items-center justify-end gap-3 flex-shrink-0 border-t border-slate-100">
              <button
                onClick={() => { setForceCloseTarget(null); setForceCloseActualCash(''); setForceCloseNotes(''); setForceCloseDenomCounts({}); }}
                disabled={forceClosing}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleForceClose}
                disabled={!!forceClosing}
                className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition flex items-center gap-2 disabled:opacity-60"
              >
                {forceClosing && <Loader2 className="w-4 h-4 animate-spin" />}
                {forceClosing ? 'Closing...' : 'Confirm Force Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Denomination View Modal */}
      {denomViewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-bold text-slate-800">
                Denomination — {denomViewTarget.userName || 'Shift'}
              </h3>
              <button onClick={() => setDenomViewTarget(null)} className="p-1 rounded-lg hover:bg-slate-100 transition">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-5">
              <DenominationView denomination={denomViewTarget.denomination} />
            </div>
            <div className="px-6 py-4 bg-slate-50 flex items-center justify-end border-t border-slate-100 flex-shrink-0">
              <button
                onClick={() => setDenomViewTarget(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Confirmation Modal */}
      {showReopen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Reopen Day</h3>
              <button onClick={() => { setShowReopen(false); setReopenReason(''); }} className="p-1 rounded-lg hover:bg-slate-100 transition">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  This will reopen the business day for{' '}
                  <span className="font-semibold">{businessDateFormatted}</span>.
                  The previous close record will be removed and the day will return to Open status.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  rows={3}
                  placeholder="Why is this day being reopened?"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowReopen(false); setReopenReason(''); }}
                disabled={reopening}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReopen}
                disabled={reopening || !reopenReason.trim()}
                className="px-5 py-2 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 transition flex items-center gap-2 disabled:opacity-60"
              >
                {reopening && <Loader2 className="w-4 h-4 animate-spin" />}
                {reopening ? 'Reopening...' : 'Confirm Reopen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── History Tab ────────────────────────────────────────────────────────────

function HistoryTab() {
  const { hasPermission } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expandedRows, setExpandedRows] = useState({});

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dayCloseService.getHistory({ fromDate, toDate, page, size: 15 });
      const data = res.data || res;
      setRows(data.content || []);
      setTotalPages(data.totalPages || 0);
      setExpandedRows({});
    } catch {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, page]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleDownloadPdf = async (businessDate) => {
    try {
      const res = await dayCloseService.downloadPdf(businessDate);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `day-close-${businessDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const toggleExpandRow = async (businessDate) => {
    if (expandedRows[businessDate]) {
      setExpandedRows((prev) => { const next = { ...prev }; delete next[businessDate]; return next; });
      return;
    }
    setExpandedRows((prev) => ({ ...prev, [businessDate]: { loading: true, shifts: [] } }));
    try {
      const res = await dayCloseService.getSummary(businessDate);
      const data = res.data || res;
      setExpandedRows((prev) => ({
        ...prev,
        [businessDate]: { loading: false, shifts: data.shifts || [], paymentBreakdown: data.paymentModeBreakdown || [] },
      }));
    } catch {
      setExpandedRows((prev) => ({ ...prev, [businessDate]: { loading: false, shifts: [], error: true } }));
    }
  };

  return (
    <>
      {/* Date filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">From</label>
          <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">To</label>
          <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(0); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400" />
        </div>
        <button onClick={() => { setPage(0); fetchHistory(); }}
          className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition">
          Filter
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">No records found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-slate-50/80 text-slate-500 text-[11px] uppercase tracking-wider">
                  <th className="text-center px-2 py-2.5 font-medium w-10"></th>
                  <th className="text-left px-4 py-2.5 font-medium">Date</th>
                  <th className="text-right px-4 py-2.5 font-medium">Revenue</th>
                  <th className="text-right px-4 py-2.5 font-medium">Bills</th>
                  <th className="text-right px-4 py-2.5 font-medium">Expected</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actual</th>
                  <th className="text-right px-4 py-2.5 font-medium">Diff</th>
                  <th className="text-left px-4 py-2.5 font-medium">Closed By</th>
                  <th className="text-left px-4 py-2.5 font-medium">Closed At</th>
                  <th className="text-center px-4 py-2.5 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const isExpanded = !!expandedRows[row.businessDate];
                  const expandData = expandedRows[row.businessDate];
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        onClick={() => toggleExpandRow(row.businessDate)}
                        className={`hover:bg-slate-50/50 transition cursor-pointer ${isExpanded ? 'bg-teal-50/30' : ''}`}
                      >
                        <td className="px-2 py-2.5 text-center">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-teal-600 mx-auto" /> : <ChevronRight className="w-4 h-4 text-slate-400 mx-auto" />}
                        </td>
                        <td className="px-4 py-2.5 font-medium text-slate-700">
                          {row.businessDate ? new Date(row.businessDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-700 tabular-nums">₹{fmt(row.totalRevenue)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{row.totalBills}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{row.expectedCash != null ? `₹${fmt(row.expectedCash)}` : '—'}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{row.actualCash != null ? `₹${fmt(row.actualCash)}` : '—'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {row.cashDifference != null ? (
                            <span className={`font-medium ${Number(row.cashDifference) === 0 ? 'text-emerald-600' : Number(row.cashDifference) < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                              ₹{fmt(row.cashDifference)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">{row.closedBy || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs tabular-nums">
                          {row.closedAt ? new Date(row.closedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownloadPdf(row.businessDate); }}
                            className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <div className="bg-slate-50 border-t border-b border-slate-200 px-6 py-4">
                              {expandData?.loading ? (
                                <div className="flex items-center justify-center py-6">
                                  <Loader2 className="w-5 h-5 text-teal-500 animate-spin" />
                                  <span className="ml-2 text-sm text-slate-400">Loading shift details...</span>
                                </div>
                              ) : expandData?.error ? (
                                <p className="text-sm text-red-500 text-center py-4">Failed to load shift details</p>
                              ) : expandData?.shifts?.length > 0 ? (
                                <div>
                                  <div className="flex items-center gap-2 mb-3">
                                    <Users className="w-4 h-4 text-slate-400" />
                                    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                      Shift Breakdown ({expandData.shifts.length} shift{expandData.shifts.length !== 1 ? 's' : ''})
                                    </h4>
                                  </div>
                                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                                    <table className="w-full text-[12px]">
                                      <thead>
                                        <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                                          <th className="text-left px-3 py-2 font-medium">User</th>
                                          <th className="text-left px-3 py-2 font-medium">Status</th>
                                          <th className="text-left px-3 py-2 font-medium">Start</th>
                                          <th className="text-left px-3 py-2 font-medium">End</th>
                                          <th className="text-right px-3 py-2 font-medium">Opening</th>
                                          <th className="text-right px-3 py-2 font-medium">Sales</th>
                                          <th className="text-right px-3 py-2 font-medium">Cash</th>
                                          <th className="text-right px-3 py-2 font-medium">Online</th>
                                          <th className="text-right px-3 py-2 font-medium">Expected</th>
                                          <th className="text-right px-3 py-2 font-medium">Actual</th>
                                          <th className="text-right px-3 py-2 font-medium">Diff</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {expandData.shifts.map((s) => (
                                          <tr key={s.shiftId} className="hover:bg-slate-50/50">
                                            <td className="px-3 py-2 font-medium text-slate-700">{s.userName || '—'}</td>
                                            <td className="px-3 py-2">
                                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${s.status === 'CLOSED' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                                {s.status}
                                              </span>
                                            </td>
                                            <td className="px-3 py-2 text-slate-500 tabular-nums">{s.startedAt ? new Date(s.startedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}</td>
                                            <td className="px-3 py-2 text-slate-500 tabular-nums">{s.endedAt ? new Date(s.endedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}</td>
                                            <td className="px-3 py-2 text-right text-slate-600 tabular-nums">₹{fmt(s.openingCash)}</td>
                                            <td className="px-3 py-2 text-right font-semibold text-slate-800 tabular-nums">₹{fmt(s.totalSales)}</td>
                                            <td className="px-3 py-2 text-right text-slate-600 tabular-nums">₹{fmt(s.cashSales)}</td>
                                            <td className="px-3 py-2 text-right text-slate-600 tabular-nums">₹{fmt(s.onlineSales)}</td>
                                            <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{s.expectedCash != null ? `₹${fmt(s.expectedCash)}` : '—'}</td>
                                            <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{s.actualCash != null ? `₹${fmt(s.actualCash)}` : '—'}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                              {s.cashDifference != null ? (
                                                <span className={`font-medium ${Number(s.cashDifference) === 0 ? 'text-emerald-600' : Number(s.cashDifference) < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                                                  ₹{fmt(s.cashDifference)}
                                                </span>
                                              ) : '—'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center py-4 text-sm text-slate-400">
                                  <Clock className="w-4 h-4 mr-2" />
                                  No shifts recorded for this date
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-xs text-slate-500">Page {page + 1} of {totalPages}</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Shared Components ──────────────────────────────────────────────────────

const fmt = (val) =>
  Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function SummaryCard({ icon: Icon, label, value, color }) {
  const iconColorMap = {
    teal: 'bg-teal-100 text-teal-600', blue: 'bg-blue-100 text-blue-600',
    violet: 'bg-violet-100 text-violet-600', emerald: 'bg-emerald-100 text-emerald-600',
    sky: 'bg-sky-100 text-sky-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-lg ${iconColorMap[color] || 'bg-teal-100 text-teal-600'}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-lg font-bold text-slate-800 truncate tabular-nums">{value}</p>
    </div>
  );
}

function ValidationPanel({ openBillCount, runningOrderCount, unpaidBillCount, isClosed, summary }) {
  if (isClosed) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4">
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <div className="text-sm text-emerald-700">
            <span className="font-semibold">Day closed</span>
            {summary?.closedBy && ` by ${summary.closedBy}`}
            {summary?.closedAt && ` at ${new Date(summary.closedAt).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, day: 'numeric', month: 'short' })}`}
            {summary?.notes && <span className="block mt-1 text-emerald-600">Notes: {summary.notes}</span>}
            {summary?.actualCash != null && (
              <div className="mt-2 flex flex-wrap gap-4 text-xs">
                <span>Expected Cash: <strong>₹{fmt(summary.expectedCash)}</strong></span>
                <span>Actual Cash: <strong>₹{fmt(summary.actualCash)}</strong></span>
                <span className={Number(summary.cashDifference) === 0 ? 'text-emerald-700' : Number(summary.cashDifference) < 0 ? 'text-red-600' : 'text-amber-600'}>
                  Difference: <strong>₹{fmt(summary.cashDifference)}</strong>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const hasBlockers = openBillCount > 0 || runningOrderCount > 0;
  const hasWarnings = unpaidBillCount > 0;

  if (!hasBlockers && !hasWarnings) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3.5 flex items-center gap-3">
        <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 flex-shrink-0" />
        <p className="text-sm text-emerald-700 font-medium">All clear — ready to close.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {runningOrderCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3.5 flex items-center gap-3">
          <AlertTriangle className="w-4.5 h-4.5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700"><span className="font-semibold">{runningOrderCount} order(s) in progress</span> — bill or cancel them before closing.</p>
        </div>
      )}
      {openBillCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3.5 flex items-center gap-3">
          <AlertTriangle className="w-4.5 h-4.5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700"><span className="font-semibold">{openBillCount} open bill(s)</span> must be finalized or cancelled before closing.</p>
        </div>
      )}
      {hasWarnings && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3.5 flex items-center gap-3">
          <AlertTriangle className="w-4.5 h-4.5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700"><span className="font-semibold">{unpaidBillCount} finalized bill(s)</span> have pending payments.</p>
        </div>
      )}
    </div>
  );
}